import { useCallback } from 'react';

import { gql } from '@apollo/client';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import { PersonActivityType, UpdatePersonInput } from '@this+that/models';

export const PersonActivityPlatform = {
  GMAIL: 'GMAIL',
  OUTLOOK: 'OUTLOOK',
  SLACK: 'SLACK',
} as const;
export type PersonActivityPlatform =
  (typeof PersonActivityPlatform)[keyof typeof PersonActivityPlatform];

export interface PersonSourceAvatar {
  source: string;
  avatarUrl: string;
}

export interface Person {
  id: string;
  displayName: string;
  origin: string;
  handle: string | null;
  emails: string[];
  // Digests dos emails acima, na mesma ordem. O socket de atividade
  // carrega o digest da contraparte em vez do endereco; comparar os dois
  // e o que mantem a aba Team viva sem endereco no fio.
  emailHmacs: string[];
  phones: string[] | null;
  notes: string | null;
  jobTitle: string | null;
  avatarUrl: string | null;
  teamId: string | null;
  version: number | null;
  sharedWithTeamIds: string[];
  isMine: boolean;
  origins: string[];
  secondaryAvatars: PersonSourceAvatar[];
}

export interface PersonActivity {
  type: PersonActivityType;
  messageId: string;
  platform: PersonActivityPlatform | null;
  direction: string | null;
  title: string | null;
  subject: string | null;
  snippet: string | null;
  counterpart: string | null;
  createdAt: number;
}

export interface PersonEventFlowStep {
  type: string;
  name: string | null;
}

export interface PersonEventFlow {
  flowId: string | null;
  name: string;
  triggerType: string | null;
  triggerKey: string | null;
  steps: PersonEventFlowStep[];
  recordedByStepIndex: number;
}

export interface PersonEvent {
  id: string;
  title: string;
  description: string | null;
  actor: string;
  isManual: boolean;
  createdAt: number;
  flow: PersonEventFlow | null;
}

export interface PersonPage {
  items: Person[];
  cursor: string | null;
}

export interface PersonEventPage {
  items: PersonEvent[];
  cursor: string | null;
}

export interface PersonActivityPage {
  items: PersonActivity[];
  cursor: string | null;
}

export interface CreatePersonInput {
  displayName: string;
  emails: string[];
  phones?: string[];
  notes?: string;
  jobTitle?: string;
}

// UpdatePersonInput from @this+that/models has no expectedVersion field — it
// is only meaningful on a team-contact edit, so it is layered on locally
// rather than widening the shared model type.
export type UpdatePersonMutationInput = UpdatePersonInput & {
  expectedVersion?: number;
};

export interface PersonEmailConflict {
  personId: string;
  email: string;
}

export type TeamContactCopyResult =
  | {
      __typename: 'TeamContactCopied';
      person: Person;
      eventsCopyQueued: boolean;
    }
  | ({ __typename: 'PersonEmailConflict' } & PersonEmailConflict);

export type CreatePersonForTeamResult =
  | {
      __typename: 'PersonCreatedWithTeamCopy';
      person: Person;
      teamPerson: Person;
    }
  | {
      __typename: 'PersonCreatedTeamCopyConflict';
      person: Person;
      conflict: PersonEmailConflict;
    };

const PERSON_FIELDS_FRAGMENT = gql`
  fragment PersonFields on Person {
    id
    displayName
    origin
    handle
    emails
    emailHmacs
    phones
    notes
    jobTitle
    avatarUrl
    teamId
    version
    sharedWithTeamIds
    isMine
    origins
    secondaryAvatars {
      source
      avatarUrl
    }
  }
`;

const PERSON_ACTIVITY_FIELDS_FRAGMENT = gql`
  fragment PersonActivityFields on PersonActivity {
    type
    messageId
    platform
    direction
    title
    subject
    snippet
    counterpart
    createdAt
  }
`;

const PERSON_EVENT_FIELDS_FRAGMENT = gql`
  fragment PersonEventFields on PersonEvent {
    id
    title
    description
    actor
    isManual
    createdAt
    flow {
      flowId
      name
      triggerType
      triggerKey
      steps {
        type
        name
      }
      recordedByStepIndex
    }
  }
`;

const LIST_MY_PEOPLE = gql`
  query listMyPeople($limit: Int!, $cursor: String, $origins: [String!]) {
    listMyPeople(limit: $limit, cursor: $cursor, origins: $origins) {
      items {
        ...PersonFields
      }
      cursor
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const LIST_TEAM_PEOPLE = gql`
  query listTeamPeople(
    $teamId: String!
    $limit: Int!
    $cursor: String
    $origins: [String!]
  ) {
    listTeamPeople(
      teamId: $teamId
      limit: $limit
      cursor: $cursor
      origins: $origins
    ) {
      items {
        ...PersonFields
      }
      cursor
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const SEARCH_MY_PEOPLE = gql`
  query searchMyPeople($query: String!, $origins: [String!]) {
    searchMyPeople(query: $query, origins: $origins) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const SEARCH_TEAM_PEOPLE = gql`
  query searchTeamPeople(
    $teamId: String!
    $query: String!
    $origins: [String!]
  ) {
    searchTeamPeople(teamId: $teamId, query: $query, origins: $origins) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const GET_PERSON = gql`
  query getPerson($id: ID!) {
    getPerson(id: $id) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const GET_PERSON_ACTIVITY = gql`
  query getPersonActivity($id: ID!, $limit: Int) {
    getPersonActivity(id: $id, limit: $limit) {
      ...PersonActivityFields
    }
  }
  ${PERSON_ACTIVITY_FIELDS_FRAGMENT}
`;

const GET_PERSON_EVENTS = gql`
  query getPersonEvents($id: ID!, $limit: Int, $cursor: String) {
    getPersonEvents(id: $id, limit: $limit, cursor: $cursor) {
      items {
        ...PersonEventFields
      }
      cursor
    }
  }
  ${PERSON_EVENT_FIELDS_FRAGMENT}
`;

const GET_PERSON_ACTIVITY_PAGE = gql`
  query getPersonActivityPage(
    $id: ID!
    $limit: Int
    $cursor: String
    $platforms: [PersonActivityPlatform!]
  ) {
    getPersonActivityPage(
      id: $id
      limit: $limit
      cursor: $cursor
      platforms: $platforms
    ) {
      items {
        ...PersonActivityFields
      }
      cursor
    }
  }
  ${PERSON_ACTIVITY_FIELDS_FRAGMENT}
`;

interface ListMyPeopleData {
  listMyPeople: PersonPage;
}

interface ListTeamPeopleData {
  listTeamPeople: PersonPage;
}

interface SearchMyPeopleData {
  searchMyPeople: Person[];
}

interface SearchTeamPeopleData {
  searchTeamPeople: Person[];
}

interface GetPersonData {
  getPerson: Person | null;
}

interface GetPersonActivityData {
  getPersonActivity: PersonActivity[];
}

interface GetPersonEventsData {
  getPersonEvents: PersonEventPage;
}

interface GetPersonActivityPageData {
  getPersonActivityPage: PersonActivityPage;
}

export function useListMyPeople() {
  const [runQuery, { loading, error, data }] = useLazyQuery<ListMyPeopleData>(
    LIST_MY_PEOPLE,
    { fetchPolicy: 'network-only' },
  );

  const listMyPeople = useCallback(
    async (params: { limit: number; cursor?: string; origins?: string[] }) => {
      const result = await runQuery({ variables: params });
      return result.data?.listMyPeople ?? null;
    },
    [runQuery],
  );

  const people: PersonPage | null = data?.listMyPeople ?? null;

  return { listMyPeople, people, loading, error };
}

export function useListTeamPeople() {
  const [runQuery, { loading, error, data }] = useLazyQuery<ListTeamPeopleData>(
    LIST_TEAM_PEOPLE,
    {
      fetchPolicy: 'network-only',
    },
  );

  const listTeamPeople = useCallback(
    async (params: {
      teamId: string;
      limit: number;
      cursor?: string;
      origins?: string[];
    }) => {
      const result = await runQuery({ variables: params });
      return result.data?.listTeamPeople ?? null;
    },
    [runQuery],
  );

  const people: PersonPage | null = data?.listTeamPeople ?? null;

  return { listTeamPeople, people, loading, error };
}

export function useSearchMyPeople() {
  const [runQuery, { loading, error, data }] = useLazyQuery<SearchMyPeopleData>(
    SEARCH_MY_PEOPLE,
    {
      fetchPolicy: 'network-only',
    },
  );

  const searchMyPeople = useCallback(
    async (params: { query: string; origins?: string[] }) => {
      const result = await runQuery({ variables: params });
      return result.data?.searchMyPeople ?? [];
    },
    [runQuery],
  );

  const results: Person[] = data?.searchMyPeople ?? [];

  return { searchMyPeople, results, loading, error };
}

export function useSearchTeamPeople() {
  const [runQuery, { loading, error, data }] =
    useLazyQuery<SearchTeamPeopleData>(SEARCH_TEAM_PEOPLE, {
      fetchPolicy: 'network-only',
    });

  const searchTeamPeople = useCallback(
    async (params: { teamId: string; query: string; origins?: string[] }) => {
      const result = await runQuery({ variables: params });
      return result.data?.searchTeamPeople ?? [];
    },
    [runQuery],
  );

  const results: Person[] = data?.searchTeamPeople ?? [];

  return { searchTeamPeople, results, loading, error };
}

export function useGetPerson() {
  const client = useApolloClient();

  const getPerson = useCallback(
    async (id: string) => {
      const result = await client.query<GetPersonData>({
        query: GET_PERSON,
        variables: { id },
        fetchPolicy: 'network-only',
      });
      return result.data?.getPerson ?? null;
    },
    [client],
  );

  return { getPerson };
}

export function useGetPersonActivity() {
  const [runQuery, { loading, error }] = useLazyQuery<GetPersonActivityData>(
    GET_PERSON_ACTIVITY,
    { fetchPolicy: 'network-only' },
  );

  const getPersonActivity = useCallback(
    async (params: { id: string; limit?: number }) => {
      const result = await runQuery({ variables: params });
      return result.data?.getPersonActivity ?? [];
    },
    [runQuery],
  );

  return { getPersonActivity, loading, error };
}

export function useLazyGetPersonActivity() {
  const [runQuery, { loading, error, data }] =
    useLazyQuery<GetPersonActivityData>(GET_PERSON_ACTIVITY, {
      fetchPolicy: 'network-only',
    });

  const getPersonActivity = useCallback(
    async (id: string, limit?: number) => {
      const result = await runQuery({ variables: { id, limit } });
      return result.data?.getPersonActivity ?? [];
    },
    [runQuery],
  );

  const activity: PersonActivity[] = data?.getPersonActivity ?? [];

  return { getPersonActivity, activity, loading, error };
}

export function useLazyGetPersonEvents() {
  const [runQuery, { loading, error }] = useLazyQuery<GetPersonEventsData>(
    GET_PERSON_EVENTS,
    { fetchPolicy: 'network-only' },
  );

  const getPersonEvents = useCallback(
    async (id: string, limit?: number, cursor?: string) => {
      const result = await runQuery({ variables: { id, limit, cursor } });
      return result.data?.getPersonEvents ?? { items: [], cursor: null };
    },
    [runQuery],
  );

  return { getPersonEvents, loading, error };
}

export function useLazyGetPersonActivityPage() {
  const [runQuery, { loading, error }] =
    useLazyQuery<GetPersonActivityPageData>(GET_PERSON_ACTIVITY_PAGE, {
      fetchPolicy: 'network-only',
    });

  const getPersonActivityPage = useCallback(
    async (
      id: string,
      limit?: number,
      cursor?: string,
      platforms?: PersonActivityPlatform[],
    ) => {
      // errorPolicy is 'all', so a non-null `data` does not mean the query succeeded
      const { data, error } = await runQuery({
        variables: { id, limit, cursor, platforms },
      });

      if (error) {
        throw error;
      }

      return data?.getPersonActivityPage ?? { items: [], cursor: null };
    },
    [runQuery],
  );

  return { getPersonActivityPage, loading, error };
}

const CREATE_PERSON = gql`
  mutation createPerson($input: CreatePersonInput!) {
    createPerson(input: $input) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const UPDATE_PERSON = gql`
  mutation updatePerson($id: ID!, $input: UpdatePersonInput!) {
    updatePerson(id: $id, input: $input) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const UPDATE_PERSON_EMAILS = gql`
  mutation updatePersonEmails(
    $id: ID!
    $emails: [String!]!
    $expectedVersion: Int
  ) {
    updatePersonEmails(
      id: $id
      emails: $emails
      expectedVersion: $expectedVersion
    ) {
      ...PersonFields
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const DELETE_PERSON = gql`
  mutation deletePerson($id: ID!) {
    deletePerson(id: $id)
  }
`;

const CREATE_PERSON_EVENT = gql`
  mutation createPersonEvent($id: ID!, $input: CreatePersonEventInput!) {
    createPersonEvent(id: $id, input: $input) {
      ...PersonEventFields
    }
  }
  ${PERSON_EVENT_FIELDS_FRAGMENT}
`;

const UPDATE_PERSON_EVENT = gql`
  mutation updatePersonEvent($id: ID!, $input: UpdatePersonEventInput!) {
    updatePersonEvent(id: $id, input: $input) {
      ...PersonEventFields
    }
  }
  ${PERSON_EVENT_FIELDS_FRAGMENT}
`;

const DELETE_PERSON_EVENT = gql`
  mutation deletePersonEvent($id: ID!, $eventId: ID!, $createdAt: Float!) {
    deletePersonEvent(id: $id, eventId: $eventId, createdAt: $createdAt)
  }
`;

const ADD_PERSON_TO_TEAM = gql`
  mutation addPersonToTeam(
    $id: ID!
    $teamId: String!
    $carryNotes: Boolean
    $carryEvents: Boolean
  ) {
    addPersonToTeam(
      id: $id
      teamId: $teamId
      carryNotes: $carryNotes
      carryEvents: $carryEvents
    ) {
      __typename
      ... on TeamContactCopied {
        person {
          ...PersonFields
        }
        eventsCopyQueued
      }
      ... on PersonEmailConflict {
        personId
        email
      }
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const CREATE_TEAM_PERSON = gql`
  mutation createTeamPerson($input: CreatePersonInput!, $teamId: String!) {
    createTeamPerson(input: $input, teamId: $teamId) {
      __typename
      ... on TeamContactCopied {
        person {
          ...PersonFields
        }
        eventsCopyQueued
      }
      ... on PersonEmailConflict {
        personId
        email
      }
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const CREATE_PERSON_FOR_TEAM = gql`
  mutation createPersonForTeam($input: CreatePersonInput!, $teamId: String!) {
    createPersonForTeam(input: $input, teamId: $teamId) {
      __typename
      ... on PersonCreatedWithTeamCopy {
        person {
          ...PersonFields
        }
        teamPerson {
          ...PersonFields
        }
      }
      ... on PersonCreatedTeamCopyConflict {
        person {
          ...PersonFields
        }
        conflict {
          personId
          email
        }
      }
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

const MERGE_PEOPLE = gql`
  mutation mergePeople($targetId: ID!, $personIds: [ID!]!) {
    mergePeople(targetId: $targetId, personIds: $personIds) {
      person {
        ...PersonFields
      }
      mergedIds
      movedTeamIds
    }
  }
  ${PERSON_FIELDS_FRAGMENT}
`;

interface CreatePersonData {
  createPerson: Person;
}

interface UpdatePersonData {
  updatePerson: Person;
}

interface UpdatePersonEmailsData {
  updatePersonEmails: Person;
}

interface DeletePersonData {
  deletePerson: boolean;
}

export interface CreatePersonEventMutationInput {
  title: string;
  description?: string;
  createdAt?: number;
}

interface CreatePersonEventData {
  createPersonEvent: PersonEvent;
}

export interface UpdatePersonEventMutationInput {
  eventId: string;
  createdAt: number;
  title?: string;
  description?: string;
}

interface UpdatePersonEventData {
  updatePersonEvent: PersonEvent;
}

interface DeletePersonEventData {
  deletePersonEvent: boolean;
}

interface AddPersonToTeamData {
  addPersonToTeam: TeamContactCopyResult;
}

export interface TeamContactCopyOptions {
  carryNotes: boolean;
  carryEvents: boolean;
}

interface CreateTeamPersonData {
  createTeamPerson: TeamContactCopyResult;
}

interface CreatePersonForTeamData {
  createPersonForTeam: CreatePersonForTeamResult;
}

export interface MergePeopleSummary {
  person: Person;
  mergedIds: string[];
  movedTeamIds: string[];
}

interface MergePeopleData {
  mergePeople: MergePeopleSummary;
}

export function useCreatePerson() {
  const [mutate, { loading, error }] =
    useMutation<CreatePersonData>(CREATE_PERSON);

  const createPerson = useCallback(
    async (input: CreatePersonInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createPerson;
    },
    [mutate],
  );

  return { createPerson, loading, error };
}

export function useUpdatePerson() {
  const [mutate, { loading, error }] =
    useMutation<UpdatePersonData>(UPDATE_PERSON);

  const updatePerson = useCallback(
    async (id: string, input: UpdatePersonMutationInput) => {
      const { data } = await mutate({ variables: { id, input } });
      return data?.updatePerson;
    },
    [mutate],
  );

  return { updatePerson, loading, error };
}

export function useUpdatePersonEmails() {
  const [mutate, { loading, error }] =
    useMutation<UpdatePersonEmailsData>(UPDATE_PERSON_EMAILS);

  const updatePersonEmails = useCallback(
    async (id: string, emails: string[], expectedVersion?: number) => {
      const { data } = await mutate({
        variables: { id, emails, expectedVersion },
      });
      return data?.updatePersonEmails;
    },
    [mutate],
  );

  return { updatePersonEmails, loading, error };
}

export function useDeletePerson() {
  const [mutate, { loading, error }] =
    useMutation<DeletePersonData>(DELETE_PERSON);

  const deletePerson = useCallback(
    async (id: string) => {
      const { data } = await mutate({ variables: { id } });
      return data?.deletePerson ?? false;
    },
    [mutate],
  );

  return { deletePerson, loading, error };
}

export function useCreatePersonEvent() {
  const [mutate, { loading, error }] =
    useMutation<CreatePersonEventData>(CREATE_PERSON_EVENT);

  const createPersonEvent = useCallback(
    async (personId: string, input: CreatePersonEventMutationInput) => {
      const { data } = await mutate({ variables: { id: personId, input } });
      return data?.createPersonEvent;
    },
    [mutate],
  );

  return { createPersonEvent, loading, error };
}

export function useUpdatePersonEvent() {
  const [mutate, { loading, error }] =
    useMutation<UpdatePersonEventData>(UPDATE_PERSON_EVENT);

  const updatePersonEvent = useCallback(
    async (id: string, input: UpdatePersonEventMutationInput) => {
      const { data } = await mutate({ variables: { id, input } });
      return data?.updatePersonEvent;
    },
    [mutate],
  );

  return { updatePersonEvent, loading, error };
}

export function useDeletePersonEvent() {
  const [mutate, { loading, error }] =
    useMutation<DeletePersonEventData>(DELETE_PERSON_EVENT);

  const deletePersonEvent = useCallback(
    async (id: string, eventId: string, createdAt: number) => {
      const { data } = await mutate({ variables: { id, eventId, createdAt } });
      return data?.deletePersonEvent ?? false;
    },
    [mutate],
  );

  return { deletePersonEvent, loading, error };
}

export function useAddPersonToTeam() {
  const [mutate, { loading, error }] =
    useMutation<AddPersonToTeamData>(ADD_PERSON_TO_TEAM);

  const addPersonToTeam = useCallback(
    async (
      id: string,
      teamId: string,
      { carryNotes, carryEvents }: TeamContactCopyOptions,
    ) => {
      const { data } = await mutate({
        variables: { id, teamId, carryNotes, carryEvents },
      });
      return data?.addPersonToTeam;
    },
    [mutate],
  );

  return { addPersonToTeam, loading, error };
}

export function useCreateTeamPerson() {
  const [mutate, { loading, error }] =
    useMutation<CreateTeamPersonData>(CREATE_TEAM_PERSON);

  const createTeamPerson = useCallback(
    async (input: CreatePersonInput, teamId: string) => {
      const { data } = await mutate({ variables: { input, teamId } });
      return data?.createTeamPerson;
    },
    [mutate],
  );

  return { createTeamPerson, loading, error };
}

export function useCreatePersonForTeam() {
  const [mutate, { loading, error }] = useMutation<CreatePersonForTeamData>(
    CREATE_PERSON_FOR_TEAM,
  );

  const createPersonForTeam = useCallback(
    async (input: CreatePersonInput, teamId: string) => {
      const { data } = await mutate({ variables: { input, teamId } });
      return data?.createPersonForTeam;
    },
    [mutate],
  );

  return { createPersonForTeam, loading, error };
}

export function useMergePeople() {
  const [mutate, { loading, error }] =
    useMutation<MergePeopleData>(MERGE_PEOPLE);

  const mergePeople = useCallback(
    async (targetId: string, personIds: string[]) => {
      const { data } = await mutate({ variables: { targetId, personIds } });
      return data?.mergePeople;
    },
    [mutate],
  );

  return { mergePeople, loading, error };
}
